import React, { useState, useEffect } from 'react';
import Navbar from './Navbar';
import { useNavigate } from 'react-router-dom';
import API_CONFIG, { buildApiUrl, buildApiUrlWithParams } from '../config/api';

const RecordPayments = () => {
    const [debts, setDebts] = useState([]);
    const [loading, setLoading] = useState(true);
    const [userGroups, setUserGroups] = useState([]);
    const [editingDebt, setEditingDebt] = useState(null);
    const [debtTypes, setDebtTypes] = useState([]);
    const [students, setStudents] = useState([]);
    const [showNewForm, setShowNewForm] = useState(false);
    const [newDebt, setNewDebt] = useState({
        student_id: '',
        debt_amount: '',
        debt_date: '',
        debt_type_id: '',
        debt_notes: '',
        debt_discount: '',
        debt_paid_date: '',
        debt_paid_amount: '',
        debt_paid_notes: ''
    });
    const [studentSearch, setStudentSearch] = useState('');
    const [showStudentDropdown, setShowStudentDropdown] = useState(false);
    const [filters, setFilters] = useState({
        debtType: '',
        minDueDate: '',
        maxDueDate: '',
        paidStatus: 'unpaid',
        studentName: ''
    });
    const navigate = useNavigate();

    useEffect(() => {
        const token = localStorage.getItem("token");
        if (!token) {
            navigate('/login');
            return;
        }

        const decoded = JSON.parse(atob(token.split(".")[1]));
        setUserGroups(decoded["cognito:groups"]);

        fetchDebtTypes();
        fetchDebts();
        fetchStudents();
    }, [navigate]);

    const fetchDebtTypes = async () => {
        try {
            const token = localStorage.getItem("token");
            const response = await fetch(buildApiUrl(API_CONFIG.ENDPOINTS.DEBT_TYPES), {
                headers: {
                    'Authorization': `Bearer ${token}`
                }
            });
            const data = await response.json();
            setDebtTypes(data);
        } catch (error) {
            console.error('Error fetching debt types:', error);
        }
    };

    const fetchDebts = async () => {
        try {
            const token = localStorage.getItem("token");
            const response = await fetch(buildApiUrl(API_CONFIG.ENDPOINTS.STUDENT_DEBTS), {
                headers: {
                    'Authorization': `Bearer ${token}`
                }
            });
            const data = await response.json();
            setDebts(data);
            setLoading(false);
        } catch (error) {
            console.error('Error fetching debts:', error);
            setLoading(false);
        }
    };

    const fetchStudents = async () => {
        try {
            const token = localStorage.getItem("token");
            const response = await fetch(buildApiUrl(API_CONFIG.ENDPOINTS.STUDENTS), {
                headers: {
                    'Authorization': `Bearer ${token}`
                }
            });
            const data = await response.json();
            setStudents(data);
        } catch (error) {
            console.error('Error fetching students:', error);
        }
    };

    const handleEdit = (debt) => {
        setEditingDebt({ ...debt });
    };

    const handleSave = async () => {
        try {
            const token = localStorage.getItem("token");
            const response = await fetch(buildApiUrl(API_CONFIG.ENDPOINTS.STUDENT_DEBTS), {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify(editingDebt)
            });

            if (response.ok) {
                setEditingDebt(null);
                fetchDebts();
            }
        } catch (error) {
            console.error('Error updating debt:', error);
        }
    };

    const handleDelete = async (debtId) => {
        if (window.confirm('Are you sure you want to delete this debt record?')) {
            try {
                const token = localStorage.getItem("token");
                const response = await fetch(buildApiUrlWithParams(API_CONFIG.ENDPOINTS.STUDENT_DEBT_DETAIL, debtId), {
                    method: 'DELETE',
                    headers: {
                        'Authorization': `Bearer ${token}`
                    }
                });

                if (response.ok) {
                    fetchDebts();
                }
            } catch (error) {
                console.error('Error deleting debt:', error);
            }
        }
    };

    const handleFilterChange = (e) => {
        const { name, value } = e.target;
        setFilters(prev => ({
            ...prev,
            [name]: value
        }));
    };

    const filteredDebts = debts.filter(debt => {
        if (filters.debtType && debt.debt_type_id !== parseInt(filters.debtType)) {
            return false;
        }
        if (filters.minDueDate && debt.debt_date < filters.minDueDate) {
            return false;
        }
        if (filters.maxDueDate && debt.debt_date > filters.maxDueDate) {
            return false;
        }
        if (filters.studentName && !debt.student_name.toLowerCase().includes(filters.studentName.toLowerCase())) {
            return false;
        }
        const isPaid = (debt.debt_amount - (debt.debt_discount || 0) - (debt.debt_paid_amount || 0)) <= 0;
        if (filters.paidStatus === 'paid' && !isPaid) {
            return false;
        }
        if (filters.paidStatus === 'unpaid' && isPaid) {
            return false;
        }
        return true;
    });

    const getDebtTypeName = (debtTypeId) => {
        const debtType = debtTypes.find(d => d.debt_type_id === debtTypeId);
        return debtType ? debtType.debt_type_desc : debtTypeId;
    };

    const handleNew = () => {
        setShowNewForm(true);
        setNewDebt({
            student_id: '',
            debt_amount: '',
            debt_date: '',
            debt_type_id: '',
            debt_notes: '',
            debt_discount: '',
            debt_paid_date: '',
            debt_paid_amount: '',
            debt_paid_notes: ''
        });
    };

    const handleSaveNew = async () => {
        try {
            const token = localStorage.getItem("token");
            const response = await fetch(buildApiUrl(API_CONFIG.ENDPOINTS.STUDENT_DEBTS), {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({
                    ...newDebt,
                    debt_amount: newDebt.debt_amount ? parseFloat(newDebt.debt_amount) : null,
                    debt_discount: newDebt.debt_discount ? parseFloat(newDebt.debt_discount) : null,
                    debt_paid_amount: newDebt.debt_paid_amount ? parseFloat(newDebt.debt_paid_amount) : null
                })
            });

            if (response.ok) {
                setShowNewForm(false);
                fetchDebts();
            }
        } catch (error) {
            console.error('Error creating new debt:', error);
        }
    };

    const handlePaid = async (debt) => {
        try {
            const token = localStorage.getItem("token");
            const today = new Date().toISOString().split('T')[0];
            const paidAmount = debt.debt_amount - (debt.debt_discount || 0);
            
            const updatedDebt = {
                ...debt,
                debt_paid_date: today,
                debt_paid_amount: paidAmount,
                debt_paid_notes: 'Marked as paid'
            };

            const response = await fetch(buildApiUrl(API_CONFIG.ENDPOINTS.STUDENT_DEBTS), {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify(updatedDebt)
            });

            if (response.ok) {
                fetchDebts();
            }
        } catch (error) {
            console.error('Error marking debt as paid:', error);
        }
    };

    if (loading) {
        return (
            <div className="container">
                <Navbar groups={userGroups}/>
                <div className="card">
                    <div className="card-body">
                        <div className="spinner-border" role="status">
                            <span className="visually-hidden">Loading...</span>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="container">
            <Navbar groups={userGroups}/>
            <div className="card">
                <div className="card-body">
                    <h2 className="card-title">Record Payments</h2>
                    
                    <div className="row mb-4">
                        <div className="col-md-2">
                            <label className="form-label">Student Name:</label>
                            <input
                                type="text"
                                className="form-control"
                                name="studentName"
                                value={filters.studentName}
                                onChange={handleFilterChange}
                                placeholder="Search by name"
                            />
                        </div>
                        <div className="col-md-2">
                            <label className="form-label">Debt Type:</label>
                            <select
                                className="form-control"
                                name="debtType"
                                value={filters.debtType}
                                onChange={handleFilterChange}
                            >
                                <option value="">All Types</option>
                                {debtTypes.map(type => (
                                    <option key={type.debt_type_id} value={type.debt_type_id}>
                                        {type.debt_type_desc}
                                    </option>
                                ))}
                            </select>
                        </div>
                        <div className="col-md-3">
                            <label className="form-label">Min Due Date:</label>
                            <input
                                type="date"
                                className="form-control"
                                name="minDueDate"
                                value={filters.minDueDate}
                                onChange={handleFilterChange}
                            />
                        </div>
                        <div className="col-md-3">
                            <label className="form-label">Max Due Date:</label>
                            <input
                                type="date"
                                className="form-control"
                                name="maxDueDate"
                                value={filters.maxDueDate}
                                onChange={handleFilterChange}
                            />
                        </div>
                        <div className="col-md-3">
                            <label className="form-label">Payment Status:</label>
                            <select
                                className="form-control"
                                name="paidStatus"
                                value={filters.paidStatus}
                                onChange={handleFilterChange}
                            >
                                <option value="unpaid">Unpaid</option>
                                <option value="paid">Paid</option>
                                <option value="all">All</option>
                            </select>
                        </div>
                    </div>

                    <div className="row mb-4">
                        <div className="col-md-12">
                            <button className="btn btn-primary" onClick={handleNew}>New Debt Record</button>
                        </div>
                    </div>

                    {showNewForm && (
                        <div className="card mb-4">
                            <div className="card-body">
                                <h5 className="card-title">New Debt Record</h5>
                                <div className="row">
                                    <div className="col-md-3">
                                        <label className="form-label">Student:</label>
                                        <div className="dropdown">
                                            <input
                                                type="text"
                                                className="form-control"
                                                value={studentSearch}
                                                onChange={(e) => {
                                                    setStudentSearch(e.target.value);
                                                    setShowStudentDropdown(true);
                                                }}
                                                onFocus={() => setShowStudentDropdown(true)}
                                                placeholder="Search by name"
                                            />
                                            {showStudentDropdown && studentSearch && (
                                                <div className="dropdown-menu show" style={{ width: '100%', maxHeight: '200px', overflowY: 'auto' }}>
                                                    {students
                                                        .filter(student => 
                                                            `${student.first_name} ${student.last_name}`
                                                                .toLowerCase()
                                                                .includes(studentSearch.toLowerCase())
                                                        )
                                                        .map(student => (
                                                            <button
                                                                key={student.student_id}
                                                                className="dropdown-item"
                                                                type="button"
                                                                onClick={() => {
                                                                    setNewDebt({ ...newDebt, student_id: student.student_id });
                                                                    setStudentSearch(`${student.first_name} ${student.last_name}`);
                                                                    setShowStudentDropdown(false);
                                                                }}
                                                            >
                                                                {student.first_name} {student.last_name}
                                                            </button>
                                                        ))
                                                    }
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                    <div className="col-md-3">
                                        <label className="form-label">Debt Type:</label>
                                        <select
                                            className="form-control"
                                            value={newDebt.debt_type_id}
                                            onChange={(e) => setNewDebt({ ...newDebt, debt_type_id: e.target.value })}
                                        >
                                            <option value="">Select Type</option>
                                            {debtTypes.map(type => (
                                                <option key={type.debt_type_id} value={type.debt_type_id}>
                                                    {type.debt_type_desc}
                                                </option>
                                            ))}
                                        </select>
                                    </div>
                                    <div className="col-md-3">
                                        <label className="form-label">Amount:</label>
                                        <input
                                            type="number"
                                            className="form-control"
                                            value={newDebt.debt_amount || ''}
                                            onChange={(e) => setNewDebt({ ...newDebt, debt_amount: e.target.value === '' ? null : e.target.value })}
                                        />
                                    </div>
                                    <div className="col-md-3">
                                        <label className="form-label">Date:</label>
                                        <input
                                            type="date"
                                            className="form-control"
                                            value={newDebt.debt_date}
                                            onChange={(e) => setNewDebt({ ...newDebt, debt_date: e.target.value })}
                                        />
                                    </div>
                                </div>
                                <div className="row mt-3">
                                    <div className="col-md-3">
                                        <label className="form-label">Discount:</label>
                                        <input
                                            type="number"
                                            className="form-control"
                                            value={newDebt.debt_discount || ''}
                                            onChange={(e) => setNewDebt({ ...newDebt, debt_discount: e.target.value === '' ? null : e.target.value })}
                                        />
                                    </div>
                                    <div className="col-md-3">
                                        <label className="form-label">Paid Amount:</label>
                                        <input
                                            type="number"
                                            className="form-control"
                                            value={newDebt.debt_paid_amount || ''}
                                            onChange={(e) => setNewDebt({ ...newDebt, debt_paid_amount: e.target.value === '' ? null : e.target.value })}
                                        />
                                    </div>
                                    <div className="col-md-3">
                                        <label className="form-label">Paid Date:</label>
                                        <input
                                            type="date"
                                            className="form-control"
                                            value={newDebt.debt_paid_date || ''}
                                            onChange={(e) => setNewDebt({ ...newDebt, debt_paid_date: e.target.value })}
                                        />
                                    </div>
                                    <div className="col-md-3">
                                        <label className="form-label">Notes:</label>
                                        <input
                                            type="text"
                                            className="form-control"
                                            value={newDebt.debt_notes || ''}
                                            onChange={(e) => setNewDebt({ ...newDebt, debt_notes: e.target.value })}
                                        />
                                    </div>
                                </div>
                                <div className="row mt-3">
                                    <div className="col-md-12">
                                        <label className="form-label">Paid Notes:</label>
                                        <input
                                            type="text"
                                            className="form-control"
                                            value={newDebt.debt_paid_notes || ''}
                                            onChange={(e) => setNewDebt({ ...newDebt, debt_paid_notes: e.target.value })}
                                        />
                                    </div>
                                </div>
                                <div className="row mt-3">
                                    <div className="col-md-12">
                                        <button className="btn btn-success" onClick={handleSaveNew}>Save</button>
                                        <button className="btn btn-secondary ms-2" onClick={() => setShowNewForm(false)}>Cancel</button>
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}

                    <div className="table-responsive">
                        <table className="table table-striped">
                            <thead>
                                <tr>
                                    <th>Student</th>
                                    <th>Debt Type</th>
                                    <th>Amount</th>
                                    <th>Discount</th>
                                    <th>Due Date</th>
                                    <th>Notes</th>
                                    <th>Paid</th>
                                    <th>Paid Amount</th>
                                    <th>Paid Date</th>
                                    <th>Paid Notes</th>
                                    <th>Last Notification</th>
                                    <th>Actions</th>
                                </tr>
                            </thead>
                            <tbody>
                                {filteredDebts.map((debt) => {
                                    const isPaid = (debt.debt_amount - (debt.debt_discount || 0) - (debt.debt_paid_amount || 0)) <= 0;
                                    return (
                                        <tr key={debt.student_debt_id}>
                                            <td>{debt.student_name}</td>
                                            <td>
                                                {editingDebt?.student_debt_id === debt.student_debt_id ? (
                                                    <select
                                                        className="form-control"
                                                        value={editingDebt.debt_type_id}
                                                        onChange={(e) => setEditingDebt({ ...editingDebt, debt_type_id: parseInt(e.target.value) })}
                                                    >
                                                        {debtTypes.map(type => (
                                                            <option key={type.debt_type_id} value={type.debt_type_id}>
                                                                {type.debt_type_desc}
                                                            </option>
                                                        ))}
                                                    </select>
                                                ) : (
                                                    getDebtTypeName(debt.debt_type_id)
                                                )}
                                            </td>
                                            <td>
                                                {editingDebt?.student_debt_id === debt.student_debt_id ? (
                                                    <input
                                                        type="number"
                                                        className="form-control"
                                                        value={editingDebt.debt_amount || ''}
                                                        onChange={(e) => setEditingDebt({ ...editingDebt, debt_amount: e.target.value === '' ? null : parseFloat(e.target.value) })}
                                                    />
                                                ) : (
                                                    `$${debt.debt_amount.toFixed(2)}`
                                                )}
                                            </td>
                                            <td>
                                                {editingDebt?.student_debt_id === debt.student_debt_id ? (
                                                    <input
                                                        type="number"
                                                        className="form-control"
                                                        value={editingDebt.debt_discount || ''}
                                                        onChange={(e) => setEditingDebt({ ...editingDebt, debt_discount: e.target.value === '' ? null : parseFloat(e.target.value) })}
                                                    />
                                                ) : (
                                                    debt.debt_discount ? `$${debt.debt_discount.toFixed(2)}` : '-'
                                                )}
                                            </td>
                                            <td>
                                                {editingDebt?.student_debt_id === debt.student_debt_id ? (
                                                    <input
                                                        type="date"
                                                        className="form-control"
                                                        value={editingDebt.debt_date}
                                                        onChange={(e) => setEditingDebt({ ...editingDebt, debt_date: e.target.value })}
                                                    />
                                                ) : (
                                                    debt.debt_date
                                                )}
                                            </td>
                                            <td>
                                                {editingDebt?.student_debt_id === debt.student_debt_id ? (
                                                    <input
                                                        type="text"
                                                        className="form-control"
                                                        value={editingDebt.debt_notes}
                                                        onChange={(e) => setEditingDebt({ ...editingDebt, debt_notes: e.target.value })}
                                                    />
                                                ) : (
                                                    debt.debt_notes
                                                )}
                                            </td>
                                            <td>
                                                <span className={`badge ${isPaid ? 'bg-success' : 'bg-danger'}`}>
                                                    {isPaid ? 'Yes' : 'No'}
                                                </span>
                                            </td>
                                            <td>
                                                {editingDebt?.student_debt_id === debt.student_debt_id ? (
                                                    <input
                                                        type="number"
                                                        className="form-control"
                                                        value={editingDebt.debt_paid_amount || ''}
                                                        onChange={(e) => setEditingDebt({ ...editingDebt, debt_paid_amount: e.target.value === '' ? null : parseFloat(e.target.value) })}
                                                    />
                                                ) : (
                                                    debt.debt_paid_amount ? `$${debt.debt_paid_amount.toFixed(2)}` : '-'
                                                )}
                                            </td>
                                            <td>
                                                {editingDebt?.student_debt_id === debt.student_debt_id ? (
                                                    <input
                                                        type="date"
                                                        className="form-control"
                                                        value={editingDebt.debt_paid_date || ''}
                                                        onChange={(e) => setEditingDebt({ ...editingDebt, debt_paid_date: e.target.value })}
                                                    />
                                                ) : (
                                                    debt.debt_paid_date || '-'
                                                )}
                                            </td>
                                            <td>
                                                {editingDebt?.student_debt_id === debt.student_debt_id ? (
                                                    <input
                                                        type="text"
                                                        className="form-control"
                                                        value={editingDebt.debt_paid_notes || ''}
                                                        onChange={(e) => setEditingDebt({ ...editingDebt, debt_paid_notes: e.target.value })}
                                                    />
                                                ) : (
                                                    debt.debt_paid_notes || '-'
                                                )}
                                            </td>
                                            <td>
                                                {debt.last_notification_date || '-'}
                                            </td>
                                            <td>
                                                {editingDebt?.student_debt_id === debt.student_debt_id ? (
                                                    <button className="btn btn-success btn-sm" onClick={handleSave}>Save</button>
                                                ) : (
                                                    <>
                                                        <button 
                                                            className="btn btn-success btn-sm" 
                                                            onClick={() => handlePaid(debt)}
                                                            disabled={debt.debt_paid_amount !== null}
                                                        >
                                                            Paid
                                                        </button>
                                                        <button 
                                                            className="btn btn-primary btn-sm ms-2" 
                                                            onClick={() => handleEdit(debt)}
                                                        >
                                                            Edit
                                                        </button>
                                                    </>
                                                )}
                                                <button className="btn btn-danger btn-sm ms-2" onClick={() => handleDelete(debt.student_debt_id)}>Delete</button>
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default RecordPayments; 